import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import {
  getStationLabelForRole,
  sortLotsForQueue,
} from '../../../lib/productionTrackingQueues';
import { normalizeWorkshopRoleType } from '../../../lib/workshopOperatorView';
import { isWorkshopQueueStationRole } from '../../../lib/workshopStationQueue';
import {
  applyProductionFilters,
  isWorkshopWideSearchActive,
} from '../../../lib/productionTrackingUtils';
import ProductionLotRow from './ProductionLotRow';

const OperatorLotQueue = ({
  batches,
  filteredBatches,
  filters,
  searchQuery,
  userRole,
  visibilityCtx,
  canOpenWorkshop,
  loading,
}) => {
  const { t } = useTranslation();

  const stationRole = useMemo(
    () =>
      normalizeWorkshopRoleType(visibilityCtx?.primaryRole) ||
      visibilityCtx?.primaryRole ||
      userRole,
    [visibilityCtx?.primaryRole, userRole]
  );

  const baseList = useMemo(() => {
    return applyProductionFilters(batches, filters, searchQuery);
  }, [batches, filters, searchQuery]);

  const queueLots = useMemo(() => sortLotsForQueue(baseList), [baseList]);
  const stationLabel = getStationLabelForRole(stationRole);
  const workshopWideSearch = isWorkshopWideSearchActive(filters, searchQuery);
  const isQueueView = isWorkshopQueueStationRole(stationRole) && !workshopWideSearch;

  if (loading) {
    return (
      <div
        className="text-center py-14 text-muted-foreground rounded-xl border border-dashed border-border"
        role="status"
        aria-live="polite"
      >
        {t('productionTracking.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-foreground tracking-tight">
            {t(
              isQueueView
                ? 'productionTracking.operator.title'
                : 'productionTracking.operator.allTitle'
            )}
          </h2>
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-semibold tabular-nums text-primary">
            {queueLots.length}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          {t(
            isQueueView
              ? 'productionTracking.operator.subtitle'
              : 'productionTracking.operator.allSubtitle',
            { station: stationLabel }
          )}
        </p>
      </div>

      {queueLots.length === 0 ? (
        <div
          className="text-center py-12 px-4 rounded-xl border border-dashed border-border bg-muted/10"
          role="status"
        >
          <Icon
            name="Package"
            size={28}
            className="mx-auto mb-3 text-muted-foreground"
          />
          <p className="text-foreground font-medium mb-1">
            {t(
              isQueueView
                ? 'productionTracking.operator.emptyTitle'
                : 'productionTracking.operator.allEmptyTitle',
              { station: stationLabel }
            )}
          </p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {t(
              isQueueView
                ? 'productionTracking.operator.emptyToProcess'
                : 'productionTracking.operator.allEmptyHint'
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {queueLots.map((batch) => (
            <ProductionLotRow
              key={batch.id}
              batch={batch}
              userRole={userRole}
              canOpenWorkshop={canOpenWorkshop}
              compact
              showStatus={!isQueueView}
            />
          ))}
        </div>
      )}

      {filteredBatches.length !== batches.length && (
        <p className="text-xs text-muted-foreground text-center">
          {t('productionTracking.filteredCount', {
            shown: filteredBatches.length,
            total: batches.length,
          })}
        </p>
      )}
    </div>
  );
};

export default OperatorLotQueue;
